import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  CircularProgress,
  Grid2,
  Paper,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { DailyTasksTatMetricId, DailyTasksTatStageCard } from "../../../../../../models/Dashboard/DailyTasks/DailyTasksTat";
import { DailyTasksStore } from "../../../../stores/dashboards/DailyTasksStore";
import {
  buildOpsTatSparklineSegments,
  formatOpsTatCount,
  formatOpsTatDays,
  formatOpsTatSignedDays,
  getOpsTatStatusColors,
  toOpsTatSparklineArea,
  toOpsTatSparklinePath,
} from "../ops-tat/opsTatFormat";
import {
  dailyTasksStatusPillSx,
  dailyTasksStatusTone,
  getDailyTasksChipColors,
} from "./dailyTasksChips";
import {
  DailyTasksHint,
  DailyTasksSectionTitle,
  DAILY_TASKS_HINTS,
} from "./DailyTasksHint";
import { dailyTasksStageUi } from "./DailyTasksStageUi";

interface DailyTasksStageGridProps {
  store: DailyTasksStore;
}

const SLA_BAR_CAP = 1.6;

const OpsTatInlineState = ({ message }: { message: string }) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="10rem"
      textAlign="center"
      border="1px dashed"
      borderColor="divider"
      borderRadius={2}
      px={2}
    >
      <CustomTypography
        variant="heading6"
        fontWeight={500}
        color={theme.palette.customColors.greyTextColor}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

const StageSparkline = ({
  trend,
  color,
}: {
  trend: Array<number | null>;
  color: string;
}) => {
  const width = 200;
  const height = 26;

  try {
    const { segments, lastPoint } = buildOpsTatSparklineSegments(
      trend,
      width,
      height,
    );

    if (segments.length === 0) {
      return (
        <Box sx={{ width: "100%", height: 26 }} aria-hidden />
      );
    }

    return (
      <Box
        component="svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        sx={{ display: "block", width: "100%", height: 26 }}
        aria-hidden
      >
        {segments.map((points, index) => {
          const area = toOpsTatSparklineArea(points, height);
          const line = toOpsTatSparklinePath(points);
          return (
            <React.Fragment key={`spark-${index}`}>
              {area ? (
                <path d={area} fill={color} opacity={0.08} />
              ) : null}
              {points.length >= 2 ? (
                <path
                  d={line}
                  fill="none"
                  stroke={color}
                  strokeWidth={1.6}
                  vectorEffect="non-scaling-stroke"
                />
              ) : null}
              {points.length === 1 ? (
                <circle
                  cx={points[0].x}
                  cy={points[0].y}
                  r={2}
                  fill={color}
                />
              ) : null}
            </React.Fragment>
          );
        })}
        {lastPoint ? (
          <circle cx={lastPoint.x} cy={lastPoint.y} r={2} fill={color} />
        ) : null}
      </Box>
    );
  } catch (error) {
    return <Box sx={{ width: "100%", height: 26 }} aria-hidden />;
  }
};

const StageSlaBar = ({ stage }: { stage: DailyTasksTatStageCard }) => {
  const theme = useTheme();
  const colors = getOpsTatStatusColors(theme, stage.status);
  const target = stage.target;
  const actual = stage.actual?.mean;

  let fillPct = 0;
  let markPct = (1 / SLA_BAR_CAP) * 100;

  try {
    if (target > 0 && actual != null && Number.isFinite(actual)) {
      const ratio = actual / target;
      fillPct = Math.min(ratio, SLA_BAR_CAP) / SLA_BAR_CAP * 100;
    }
    if (target > 0) {
      markPct = (1 / SLA_BAR_CAP) * 100;
    }
  } catch (error) {
    fillPct = 0;
  }

  return (
    <Box
      sx={{
        position: "relative",
        height: 4,
        borderRadius: "2px",
        bgcolor: theme.palette.customColors.veryLightGray,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: `${fillPct}%`,
          height: "100%",
          borderRadius: "2px",
          bgcolor: colors.fg,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: -2,
          left: `${markPct}%`,
          width: "1px",
          height: 8,
          bgcolor: theme.palette.customColors.navyText,
        }}
      />
    </Box>
  );
};

const StageCard = ({
  stage,
  onOpen,
}: {
  stage: DailyTasksTatStageCard;
  onOpen: () => void;
}) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;
  const colors = getOpsTatStatusColors(theme, stage.status);
  const pill = getDailyTasksChipColors(
    theme,
    dailyTasksStatusTone(stage.status),
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    try {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen();
      }
    } catch (error) {
      // ignore keyboard handler failures
    }
  };

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor:
          stage.applies_to === "custom"
            ? customColors.softCardBorder
            : "divider",
        borderStyle: stage.applies_to === "custom" ? "dashed" : "solid",
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: customColors.softCardBackground,
        "&:hover": {
          borderColor: customColors.purpleMain,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          color={customColors.greyTextColor}
        >
          {stage.code}
        </CustomTypography>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: colors.fg,
            flexShrink: 0,
          }}
        />
        <CustomTypography
          variant="heading8"
          color={customColors.navyText}
          title={stage.name}
        >
          {stage.name}
        </CustomTypography>
        {DAILY_TASKS_HINTS[stage.stageId as keyof typeof DAILY_TASKS_HINTS] ? (
          <DailyTasksHint
            text={
              DAILY_TASKS_HINTS[stage.stageId as keyof typeof DAILY_TASKS_HINTS]
            }
          />
        ) : null}
      </Box>

      {!stage.ready || !stage.actual ? (
        <CustomTypography
          variant="body2"
          color={customColors.greyTextColor}
        >
          Pending — not enough completed samples.
        </CustomTypography>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <CustomTypography
              variant="heading3"
              color={customColors.navyText}
              sx={{ letterSpacing: "-0.03em" }}
            >
              {stage.actual.mean.toFixed(1)}
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              d mean
            </CustomTypography>
            <Box
              sx={{
                ...dailyTasksStatusPillSx(theme, stage.status),
                ml: "auto",
              }}
            >
              <CustomTypography
                variant="caption"
                fontWeight={600}
                color={pill.fg}
              >
                {formatOpsTatSignedDays(stage.delta)}
              </CustomTypography>
            </Box>
          </Box>

          <StageSlaBar stage={stage} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              SLA {formatOpsTatDays(stage.target)}
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              P90 {formatOpsTatDays(stage.actual.p90)}
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              {formatOpsTatCount(stage.n)}
            </CustomTypography>
          </Box>
        </>
      )}

      <StageSparkline trend={stage.trend} color={colors.fg} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <CustomTypography
          variant="caption"
          color={customColors.greyTextColor}
          sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
        >
          {stage.owner}
        </CustomTypography>
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          8-week trend
        </CustomTypography>
      </Box>

      {stage.open !== undefined ? (
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          Open {formatOpsTatCount(stage.open)}
        </CustomTypography>
      ) : null}

      {stage.size_driven && stage.per_unit ? (
        <CustomTypography variant="caption" color={customColors.greyTextColor}>
          {formatOpsTatDays(stage.per_unit.days_per_basis.p50)} /{" "}
          {stage.per_unit.basis} u
        </CustomTypography>
      ) : null}
    </Paper>
  );
};

const DailyTasksStageGrid: React.FC<DailyTasksStageGridProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const customColors = theme.palette.customColors;
    const stages = store.summaryResult?.data.stages ?? [];
    const stageUi = dailyTasksStageUi(store.filters.stage);
    const stageTimesSubtitle = stageUi.stageGridNote;

    const openStage = async (stage: { stageId: string; name: string }) => {
      try {
        await store.openDrawer(stage.stageId as DailyTasksTatMetricId, {
          cardTitle: stage.name,
          cardSection: "stage_turnaround",
        });
      } catch (error) {
        // drilldownError is set on the store
      }
    };

    const renderBody = () => {
      if (store.summaryError && stages.length === 0) {
        return <OpsTatInlineState message={store.summaryError} />;
      }

      if (store.summaryLoading && stages.length === 0) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="10rem"
          >
            <CircularProgress size={28} />
          </Box>
        );
      }

      if (stages.length === 0) {
        return (
          <OpsTatInlineState message="No stage turnaround data for the current filters." />
        );
      }

      return (
        <Grid2 container spacing={2} columns={12}>
          {stages.map((stage) => (
            <Grid2
              key={stage.stageId}
              size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
            >
              <StageCard
                stage={stage}
                onOpen={() => {
                  void openStage(stage);
                }}
              />
            </Grid2>
          ))}
        </Grid2>
      );
    };

    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          position: "relative",
        }}
      >
        {store.summaryLoading && stages.length > 0 ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.45)",
              zIndex: 1,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <DailyTasksSectionTitle
              title="Stage times"
              hint="Average days for each step, compared with its SLA."
              subtitle={stageTimesSubtitle}
            />
          </Box>
          {stages.length > 0 ? (
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
              sx={{ letterSpacing: "0.06em", textTransform: "uppercase" }}
            >
              {stages.length} steps
            </CustomTypography>
          ) : null}
        </Box>

        {renderBody()}
      </Paper>
    );
  },
);

export default DailyTasksStageGrid;
